import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import ChapterList from './ChapterList';
import ChapterEditorForm from './ChapterEditorForm';
import {
  chaptersFromForm,
  chaptersToForm,
  normalizeChapters,
} from '../../utils/videoTime';
import { updateSessionChapters } from '../../services/training';

const EMPTY_ROW = { timestamp: '', title: '' };

export default function SessionChaptersPanel({
  sessionId,
  chapters: initialChapters,
  durationSeconds,
  canEdit,
  videoRef,
  currentTime,
  onChaptersUpdated,
}) {
  const chapters = useMemo(
    () => normalizeChapters(initialChapters),
    [initialChapters],
  );
  const chaptersKey = useMemo(
    () => chapters.map((ch) => `${ch.startSeconds}:${ch.title}`).join('|'),
    [chapters],
  );
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState(() =>
    chapters.length ? chaptersToForm(chapters) : [{ ...EMPTY_ROW }],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!editing) {
      setRows(chapters.length ? chaptersToForm(chapters) : [{ ...EMPTY_ROW }]);
    }
  }, [chaptersKey, editing]);

  const handleChapterClick = (startSeconds) => {
    const video = videoRef?.current;
    if (!video) return;
    video.currentTime = startSeconds;
    video.play().catch(() => {});
  };

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }]);

  const removeRow = (index) => {
    setRows((prev) => (prev.length <= 1 ? [{ ...EMPTY_ROW }] : prev.filter((_, i) => i !== index)));
  };

  const handleCancel = () => {
    setEditing(false);
    setError('');
    setRows(chapters.length ? chaptersToForm(chapters) : [{ ...EMPTY_ROW }]);
  };

  const handleSave = async () => {
    setError('');
    try {
      let parsed;
      try {
        parsed = chaptersFromForm(rows);
      } catch (err) {
        setError(err.message);
        return;
      }

      if (durationSeconds) {
        const over = parsed.find((ch) => ch.startSeconds > durationSeconds);
        if (over) {
          setError(`El capítulo "${over.title}" excede la duración del video`);
          return;
        }
      }

      setSaving(true);
      const updated = await updateSessionChapters(sessionId, parsed);
      onChaptersUpdated?.(updated);
      setEditing(false);
    } catch (err) {
      setError(err.message || 'No se pudieron guardar los capítulos');
    } finally {
      setSaving(false);
    }
  };

  if (!chapters.length && !canEdit) return null;

  return (
    <div className="bg-[var(--bg-base)]">
      {canEdit && (
        <div className="px-4 md:px-6 pt-4 flex items-center justify-between gap-4">
          <p className="text-xs text-[var(--text-secondary)]">
            {chapters.length
              ? `${chapters.length} capítulo${chapters.length === 1 ? '' : 's'}`
              : 'Sin capítulos — añade marcas de tiempo para facilitar la navegación'}
          </p>
          {!editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-brand-cyan hover:text-brand-green flex-shrink-0"
            >
              <SafeIcon name="Pencil" className="w-3.5 h-3.5" />
              {chapters.length ? 'Editar capítulos' : 'Añadir capítulos'}
            </button>
          ) : (
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-main)]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 gfi-gradient-bg text-brand-navy px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <SafeIcon name="Loader" className="w-3.5 h-3.5 animate-spin" />
                    Guardando…
                  </>
                ) : (
                  'Guardar'
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mx-4 md:mx-6 mt-3 p-3 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      {editing ? (
        <div className="px-4 md:px-6 py-4">
          <ChapterEditorForm
            rows={rows}
            onChange={updateRow}
            onAdd={addRow}
            onRemove={removeRow}
            disabled={saving}
            maxDurationSeconds={durationSeconds}
          />
        </div>
      ) : (
        <ChapterList
          chapters={chapters}
          currentTime={currentTime}
          onChapterClick={handleChapterClick}
        />
      )}
    </div>
  );
}
